interface ProductlaneConfig {
  widgetKey: string
  user?: {
    email?: string
  }
  theme?: Record<string, string>
}

type ProductlaneMethod = keyof Omit<ProductlaneInstance, 'queue'>

interface ProductlaneInstance {
  init: (this: ProductlaneInstance, config: ProductlaneConfig) => void
  set: (this: ProductlaneInstance, config: Partial<ProductlaneConfig>) => void
  open: (this: ProductlaneInstance) => void
  close: (this: ProductlaneInstance) => void
  toggle: (this: ProductlaneInstance) => void
  on: (this: ProductlaneInstance, event: string, callback: (...args: unknown[]) => void) => void
  off: (this: ProductlaneInstance, event: string, callback: (...args: unknown[]) => void) => void
  queue: Record<string, { args: unknown[] }>
}

interface Logger {
  warn: (message: string, ...args: unknown[]) => void
}

declare global {
  interface Window {
    Productlane: ProductlaneInstance
  }
}

declare module '#app' {
  interface NuxtApp {
    $logger: Logger
  }
}

export default defineNuxtPlugin((_nuxtApp) => {
  const isInitialized = ref(false)
  const { $logger } = useNuxtApp()
  const config = useRuntimeConfig()
  const userStore = useUserStore()
  const route = useRoute()

  // Do not initialize on share pages
  if (route.path.startsWith('/s/')) {
    return
  }

  // Initialize Productlane queue
  const productlaneQueue: Record<string, { args: unknown[] }> = {}
  const methods = ['set', 'open', 'close', 'toggle', 'on', 'off', 'init'] as const

  // Initialize the global Productlane object
  window.Productlane = {
    queue: productlaneQueue,
    ...Object.fromEntries(
      methods.map((m) => [
        m,
        (...args: unknown[]) => {
          productlaneQueue[m] = { args }
        },
      ])
    ),
  } as ProductlaneInstance

  // Function to update user email in Productlane
  const updateUserEmail = () => {
    if (!isInitialized.value) return

    if (userStore.isLoggedIn && userStore.email) {
      window.Productlane.set({
        user: {
          email: userStore.email,
        },
      })
    }
  }

  // Function to check if user should have access to ProductLane
  const shouldInitializeProductlane = computed(() => {
    return userStore.isLoggedIn
  })

  // Watch for changes in user status and initialize/cleanup ProductLane accordingly
  watch(
    shouldInitializeProductlane,
    (shouldInit) => {
      if (shouldInit && !isInitialized.value) {
        // Add script to head only when user has access
        useHead({
          script: [
            {
              src: 'https://widget.productlane.com/latest.productlane-widget.min.js',
              async: true,
              defer: true,
              crossorigin: 'anonymous',
              onload: () => {
                // Initialize Productlane after script loads
                window.Productlane.init({
                  widgetKey: config.public.productlaneKey,
                  user: userStore.email
                    ? {
                        email: userStore.email,
                      }
                    : undefined,
                  theme: {
                    '--text-color-primary': 'rgb(17, 24, 39)',
                    '--text-color-primary-dark': '#FFFFFF',
                    '--text-color-secondary': 'rgb(156, 163, 175)',
                    '--text-color-secondary-dark': 'rgb(156, 163, 175)',
                    '--text-color-highlight': 'rgb(22, 146, 164)',
                    '--text-color-highlight-dark': 'rgb(22, 146, 164)',
                    '--background-color-primary': '#FFFFFF',
                    '--background-color-primary-dark': 'rgb(17, 24, 39)',
                    '--background-color-secondary': 'rgb(243, 244, 246)',
                    '--background-color-secondary-dark': 'rgb(31, 41, 55)',
                    '--border-color-primary': 'rgb(229, 231, 235)',
                    '--border-color-primary-dark': 'rgb(31, 41, 55)',
                  },
                })

                // Listen for the loaded event
                window.Productlane.on('loaded', () => {
                  isInitialized.value = true
                  // Update email in case user was already logged in
                  updateUserEmail()
                })
              },
            },
          ],
        })
      } else if (!shouldInit && isInitialized.value) {
        // Clean up ProductLane when user loses access
        isInitialized.value = false
        // Remove the widget if it exists
        const widget = document.querySelector('script[src*="productlane-widget"]')
        if (widget) {
          widget.remove()
        }
        // Reset the Productlane instance
        window.Productlane = {
          queue: {},
          ...Object.fromEntries(
            methods.map((m) => [
              m,
              (...args: unknown[]) => {
                productlaneQueue[m] = { args }
              },
            ])
          ),
        } as ProductlaneInstance
      }
    },
    { immediate: true }
  )

  // Watch for user profile changes to update email
  watch(
    () => [userStore.isLoggedIn, userStore.email],
    () => updateUserEmail(),
    { immediate: true }
  )

  // Safe method wrapper with overloads for different method types
  function safeMethod(method: 'init'): (config: ProductlaneConfig) => void
  function safeMethod(method: 'set'): (config: Partial<ProductlaneConfig>) => void
  function safeMethod(method: 'open' | 'close' | 'toggle'): () => void
  function safeMethod(
    method: 'on' | 'off'
  ): (event: string, callback: (...args: unknown[]) => void) => void
  function safeMethod(method: ProductlaneMethod) {
    return (...args: unknown[]) => {
      if (!isInitialized.value) {
        $logger.warn('Productlane is not initialized yet')
        return
      }
      const fn = window.Productlane[method]
      if (typeof fn === 'function') {
        // @ts-ignore
        return fn.bind(window.Productlane)(...args)
      }
    }
  }

  // Provide composable functionality
  return {
    provide: {
      productlane: {
        isInitialized: readonly(isInitialized),
        open: safeMethod('open'),
        close: safeMethod('close'),
        toggle: safeMethod('toggle'),
        on: safeMethod('on'),
        off: safeMethod('off'),
      },
    },
  }
})
